import React from 'react';
import { format } from 'date-fns';
import {
  FaEdit, FaPercentage, FaMapMarkerAlt,
  FaMale, FaFemale, FaIdBadge, FaPhone, FaGlobe, FaUser, FaCalendarAlt,
  FaBuilding, FaUserAlt, FaIdCard, FaFolder, FaUsers, FaMapPin, FaBriefcase
} from 'react-icons/fa';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { UserModel } from '../../../models/UserModel';
import ProfessionalDocumentUpload from '../../register/ProfessionalDocumentUpload';

interface PersonalInfoTabProps {
  localUser: UserModel;
  editingMode: boolean;
  saving: boolean;
  isValidatingRcs: boolean;
  uploading: boolean;
  primaryColor: string;
  profDocs: {
    kbisFile: File | null;
    identityRecto: File | null;
    identityVerso: File | null;
    identityType: 'cin' | 'passeport';
    ribFile: File | null;
  };
  ribValues: { rib?: string; bic?: string };
  editingDiscountLimit: boolean;
  tempDiscountLimit: number;
  savingDiscountLimit: boolean;
  user: UserModel;
  onChangeLocal: (field: string, value: any) => void;
  onRcsChange: (rcsValue: string) => Promise<void>;
  onEditModeToggle: () => void;
  onSaveAll: () => Promise<void>;
  onCancelEdit: () => void;
  onDocumentsChange: (docs: any) => void;
  onShowRibModal: () => void;
  onEditDiscountLimit: (editing: boolean) => void;
  onSaveDiscountLimit: () => Promise<void>;
  onDiscountLimitChange: (value: number) => void;
}

const PersonalInfoTab: React.FC<PersonalInfoTabProps> = ({
  localUser,
  editingMode,
  saving,
  isValidatingRcs,
  uploading,
  primaryColor,
  profDocs,
  ribValues,
  editingDiscountLimit,
  tempDiscountLimit,
  savingDiscountLimit,
  user,
  onChangeLocal,
  onRcsChange,
  onEditModeToggle,
  onSaveAll,
  onCancelEdit,
  onDocumentsChange,
  onShowRibModal,
  onEditDiscountLimit,
  onSaveDiscountLimit,
  onDiscountLimitChange,
}) => {
  return (
    <>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <tbody>
            <tr>
              <th><FaUsers size={18} className="me-2" />Type de compte</th>
              <td>
                {editingMode ? (
                  <select
                    className="form-select"
                    value={localUser.user_type || ''}
                    onChange={(e) => onChangeLocal('user_type', e.target.value)}
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="particulier">Particulier</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="professionnel">Professionnel</option>
                  </select>
                ) : (
                  <span
                    className={`badge ${localUser.user_type === 'entreprise'
                      ? 'bg-primary'
                      : localUser.user_type === 'professionnel'
                        ? 'bg-success'
                        : 'bg-secondary'
                      }`}
                  >
                    {localUser.user_type === 'entreprise'
                      ? 'Entreprise'
                      : localUser.user_type === 'professionnel'
                        ? 'Professionnel'
                        : 'Particulier'}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th><FaUserAlt size={18} className="me-2" />Nom</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.name || ''}
                    onChange={(e) => onChangeLocal('name', e.target.value)}
                  />
                ) : (
                  localUser.name || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaUser size={18} className="me-2" />Prénom</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.firstname || ''}
                    onChange={(e) => onChangeLocal('firstname', e.target.value)}
                  />
                ) : (
                  localUser.firstname || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th>
                {localUser.civility === "Madame" ? (
                  <FaFemale size={18} className="me-2" />
                ) : (
                  <FaMale size={18} className="me-2" />
                )}
                Civilité
              </th>
              <td>
                {editingMode ? (
                  <select
                    className="form-select"
                    value={localUser.civility || ''}
                    onChange={(e) => onChangeLocal('civility', e.target.value)}
                  >
                    <option value="">--</option>
                    <option value="Monsieur">Monsieur</option>
                    <option value="Madame">Madame</option>
                  </select>
                ) : (
                  localUser.civility || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaBriefcase size={18} className="me-2" />Profession </th>
              <td>{localUser.profession || "Aucune information disponible"}</td>
            </tr>
            <tr>
              <th><FaIdBadge size={18} className="me-2" />Référence</th>
              <td>{localUser.reference || "Aucune information disponible"}</td>
            </tr>
            <tr>
              <th><FaPhone size={18} className="me-2" />Téléphone</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.number || ''}
                    onChange={(e) => onChangeLocal('number', e.target.value)}
                  />
                ) : (
                  localUser.number || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaMapPin size={18} className="me-2" />Code Postal</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.postal_code || ''}
                    onChange={(e) => onChangeLocal('postal_code', e.target.value)}
                  />
                ) : (
                  localUser.postal_code || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaGlobe size={18} className="me-2" />Ville</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.country || ''}
                    onChange={(e) => onChangeLocal('country', e.target.value)}
                  />
                ) : (
                  localUser.country || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaMapMarkerAlt size={18} className="me-2" />Adresse</th>
              <td>
                {editingMode ? (
                  <input
                    className="form-control"
                    value={localUser.adresse || ''}
                    onChange={(e) => onChangeLocal('adresse', e.target.value)}
                  />
                ) : (
                  localUser.adresse || "Aucune information disponible"
                )}
              </td>
            </tr>
            <tr>
              <th><FaCalendarAlt size={18} className="me-2" />Date de Création</th>
              <td>
                {localUser.createdAt
                  ? format(new Date(localUser.createdAt), "dd-MM-yyyy")
                  : "Aucune information disponible"}
              </td>
            </tr>

            {(localUser.user_type === "entreprise" || localUser.user_type === "personne" || localUser.user_type === "professionnel") && (
              <>
                <tr>
                  <th><FaIdCard size={18} className="me-2" />Code SIREN</th>
                  <td>
                    {editingMode ? (
                      <div className="d-flex align-items-center">
                        <input
                          className={`form-control ${localUser.user_type === 'entreprise' && !(localUser as any).code_SIREN ? 'is-invalid' : ''}`}
                          value={(localUser as any).code_SIREN || ''}
                          onChange={(e) => onRcsChange(e.target.value)}
                          placeholder={localUser.user_type === 'entreprise' ? "Code SIREN (obligatoire)" : "Code SIREN (optionnel)"}
                          maxLength={9}
                        />
                        {isValidatingRcs && (
                          <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                      </div>
                    ) : (
                      (localUser as any).code_SIREN || "Aucune information disponible"
                    )}
                  </td>
                </tr>
                <tr>
                  <th><FaBuilding size={18} className="me-2" />Raison Sociale</th>
                  <td>
                    {editingMode ? (
                      <input
                        className={`form-control ${localUser.user_type === 'entreprise' && !(localUser as any).raison_social ? 'is-invalid' : ''}`}
                        value={(localUser as any).raison_social || ''}
                        onChange={(e) => onChangeLocal('raison_social', e.target.value)}
                        placeholder={localUser.user_type === 'entreprise' ? "Raison sociale (obligatoire)" : "Raison sociale (optionnel)"}
                        disabled={isValidatingRcs}
                      />
                    ) : (
                      (localUser as any).raison_social || "Aucune information disponible"
                    )}
                  </td>
                </tr>

                {/* Section limite de remise */}
                <tr>
                  <th><FaPercentage size={18} className="me-2" />Limite de remise maximale</th>
                  <td>
                    {editingDiscountLimit ? (
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="number"
                          className="form-control"
                          value={tempDiscountLimit}
                          onChange={(e) => onDiscountLimitChange(Number(e.target.value))}
                          placeholder="Pourcentage max (0-100)"
                          min={0}
                          max={100}
                          step={0.01}
                        />
                        <Button
                          size="sm"
                          onClick={onSaveDiscountLimit}
                          disabled={savingDiscountLimit}
                          style={{ backgroundColor: '#2a919d', border: 'none', whiteSpace: 'nowrap' }}
                        >
                          {savingDiscountLimit ? <Spinner animation="border" size="sm" /> : '✓'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            onEditDiscountLimit(false);
                            onDiscountLimitChange((user as any).discount_limit_percentage || 0);
                          }}
                        >
                          ✗
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="badge bg-info me-2">
                            {(localUser as any).discount_limit_percentage || 0}%
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onEditDiscountLimit(true)}
                        >
                          <FaEdit size={14} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              </>
            )}

            {/* Champs spécifiques aux entreprises uniquement */}
            {localUser.user_type === "entreprise" && (
              <>
                <tr>
                  <th><FaFolder size={18} className="me-2" />Code NAF</th>
                  <td>
                    {editingMode ? (
                      <input
                        className="form-control"
                        value={(localUser as any).code_NAF || ''}
                        onChange={(e) => onChangeLocal('code_NAF', e.target.value)}
                        placeholder="Code NAF"
                      />
                    ) : (
                      (localUser as any).code_NAF || "Aucune information disponible"
                    )}
                  </td>
                </tr>
                <tr>
                  <th><FaFolder size={18} className="me-2" />N° TVA</th>
                  <td>
                    {editingMode ? (
                      <input
                        className="form-control"
                        value={(localUser as any).tv_intra || ''}
                        onChange={(e) => onChangeLocal('tv_intra', e.target.value)}
                        placeholder="N° TVA"
                      />
                    ) : (
                      (localUser as any).tv_intra || "Aucune information disponible"
                    )}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Section upload pour professionnels */}
      {editingMode && localUser.user_type === 'professionnel' && (
        <div className="mb-3">
          <h5 style={{ color: primaryColor }}>Documents professionnels (upload administrateur)</h5>
          <ProfessionalDocumentUpload
            onDocumentsChange={onDocumentsChange}
          />
          <div className="d-flex gap-2 mt-2">
            <Button
              onClick={onShowRibModal}
              style={{ backgroundColor: '#2A919D', color: 'white' }}
            >
              {ribValues.rib ? 'Modifier coordonnées bancaires' : 'Ajouter coordonnées bancaires'}
            </Button>
            <div className="align-self-center">
              <small className="text-muted">
                Vos coordonnées bancaires sont stockées de manière sécurisée et uniquement utilisées pour le remboursement.
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="d-flex justify-content-center gap-2 mt-2">
        {!editingMode ? (
          <Button
            onClick={onEditModeToggle}
            className="btn-edit"
          >
            <FaEdit /> Modifier
          </Button>
        ) : (
          <>
            <Button
              onClick={onSaveAll}
              disabled={saving || isValidatingRcs || uploading}
              className="btn-primary-custom"
            >
              {saving || uploading ? <Spinner animation="border" size="sm" /> : 'Sauvegarder'}
            </Button>
            <Button variant="outline-secondary" onClick={onCancelEdit}>
              Annuler
            </Button>
          </>
        )}
      </div>

      {/* Messages de validation */}
      {editingMode && localUser.user_type === 'entreprise' && (
        <div className="mt-3">
          <Alert variant="info">
            <small>
              <strong>Note :</strong> Pour un compte entreprise, le code SIREN (9 chiffres) et la raison sociale sont obligatoires.
              {isValidatingRcs && " Récupération automatique des données (raison sociale, code NAF, adresse) en cours..."}
              {!isValidatingRcs && " Saisissez un code SIREN valide pour auto-remplir les informations."}
            </small>
          </Alert>
        </div>
      )}
    </>
  );
};

export default PersonalInfoTab;